import { useSyncExternalStore } from 'react';

/**
 * Local, per-listener playback settings for a voice room — they affect only
 * what *you* hear, never what others hear, so they live entirely client-side
 * (localStorage), no server round-trip.
 *
 *  - userVolume[userId]   0..100 — how loud each participant is for you
 *  - localMuted[userId]   true   — you've silenced this participant locally
 *  - channelVolume[chId]  0..100 — a master multiplier over everyone in a room
 *
 * Effective gain for an <audio> element is (userVolume × channelVolume) / 100²,
 * clamped to [0, 1]. We intentionally cap at 100% (no boost) — boosting past
 * 1.0 needs a WebAudio GainNode and is a separate, riskier change.
 */
export interface VoiceLocalAudioState {
  userVolume: Record<string, number>;
  localMuted: Record<string, boolean>;
  channelVolume: Record<string, number>;
}

const STORAGE_KEY = 'bunker.voiceLocalAudio.v1';
const DEFAULT_VOLUME = 100;

const EMPTY: VoiceLocalAudioState = { userVolume: {}, localMuted: {}, channelVolume: {} };

let cache: VoiceLocalAudioState | null = null;
const listeners = new Set<() => void>();

function load(): VoiceLocalAudioState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as Partial<VoiceLocalAudioState>;
    return {
      userVolume: parsed.userVolume ?? {},
      localMuted: parsed.localMuted ?? {},
      channelVolume: parsed.channelVolume ?? {},
    };
  } catch {
    return { ...EMPTY };
  }
}

function getState(): VoiceLocalAudioState {
  if (!cache) cache = load();
  return cache;
}

function commit(next: VoiceLocalAudioState) {
  cache = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* private mode — in-memory only */
  }
  for (const fn of listeners) fn();
}

/** Per-slider range: 0..200% (100% = unchanged, >100% boosts via GainNode). */
export const MAX_VOLUME = 200;
const clampVolume = (v: number) => Math.max(0, Math.min(MAX_VOLUME, Math.round(v)));

export function getUserVolume(userId: string): number {
  return getState().userVolume[userId] ?? DEFAULT_VOLUME;
}

export function isLocallyMuted(userId: string): boolean {
  return getState().localMuted[userId] ?? false;
}

export function getChannelVolume(channelId: string): number {
  return getState().channelVolume[channelId] ?? DEFAULT_VOLUME;
}

/**
 * Effective WebAudio gain for a participant.
 *
 * Below 100% it's linear attenuation. Above 100% the boost ramps up faster
 * so 200% is clearly louder — a flat ×2 (+6 dB) felt weak. The curve maps the
 * raw product so 200% ≈ ×4 (+12 dB). A limiter on the output (VoiceAudioLayer)
 * keeps the boost from clipping. Values >1 need a GainNode — `<audio>.volume`
 * caps at 1.0. Capped at 8 to bound extreme stacking.
 */
export function effectiveGain(userId: string, channelId: string | null): number {
  if (isLocallyMuted(userId)) return 0;
  const user = getUserVolume(userId) / 100;
  const master = channelId ? getChannelVolume(channelId) / 100 : 1;
  const raw = user * master;
  const boosted = raw <= 1 ? raw : 1 + (raw - 1) * 3;
  return Math.max(0, Math.min(8, boosted));
}

export function setUserVolume(userId: string, volume: number) {
  const state = getState();
  commit({ ...state, userVolume: { ...state.userVolume, [userId]: clampVolume(volume) } });
}

export function setLocalMuted(userId: string, muted: boolean) {
  const state = getState();
  commit({ ...state, localMuted: { ...state.localMuted, [userId]: muted } });
}

export function toggleLocalMute(userId: string) {
  setLocalMuted(userId, !isLocallyMuted(userId));
}

export function setChannelVolume(channelId: string, volume: number) {
  const state = getState();
  commit({
    ...state,
    channelVolume: { ...state.channelVolume, [channelId]: clampVolume(volume) },
  });
}

export function subscribeVoiceLocalAudio(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Reactive snapshot for React components (re-renders on any change). */
export function useVoiceLocalAudio(): VoiceLocalAudioState {
  return useSyncExternalStore(subscribeVoiceLocalAudio, getState, getState);
}
